import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiArrowLeft, FiCalendar, FiClock, FiUsers,
  FiTrendingUp, FiBarChart2, FiAward, FiSearch, FiTag,
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { getEventContributions } from '../../services/api';
import styles from './EventContributions.module.css';

import { fmtCurrency } from '../../utils/formatNumber';
// -- Helpers -------------------------------------------------------------------

const toLocalDate = (d) => {
  if (!d) return null;
  const s = typeof d === 'string' ? d : d.toISOString();
  const [y, m, day] = s.substring(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day);
};

const fmt = (d) =>
  toLocalDate(d)?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) ?? '—';

const fmtAmt = (n) =>
  fmtCurrency(n || 0);

const STATUS_LABELS = { pending: 'Pending', partial: 'Partial', completed: 'Completed', cancelled: 'Cancelled' };

const TABS = [
  { id: 'top-donors',      label: 'Top Donors' },
  { id: 'contributors',    label: 'All Contributors' },
  { id: 'donation-wise',   label: 'Donation Wise' },
  { id: 'donation-count',  label: 'Donation Count' },
];

// -- Sub-components ------------------------------------------------------------

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className={`${styles.statCard} ${accent ? styles.statCardAccent : ''}`}>
    <div className={styles.statIcon}><Icon size={18} /></div>
    <div className={styles.statValue}>{value}</div>
    <div className={styles.statLabel}>{label}</div>
  </div>
);

const StatusBadge = ({ status }) => (
  <span className={`${styles.statusBadge} ${styles[`status_${status}`]}`}>
    {STATUS_LABELS[status] || status}
  </span>
);

// -- Tab panels ----------------------------------------------------------------

const TopDonorsTab = ({ donors, memberSearch, showPaid }) => {
  const filtered = useMemo(() => {
    if (!memberSearch.trim()) return donors;
    const q = memberSearch.toLowerCase();
    return donors.filter((d) => (d.memberName || '').toLowerCase().includes(q));
  }, [donors, memberSearch]);

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>Rank</th>
            <th className={styles.th}>Member</th>
            <th className={`${styles.th} ${styles.amtRight}`}>Offered</th>
            {showPaid && <th className={`${styles.th} ${styles.amtRight}`}>Paid</th>}
            <th className={`${styles.th} ${styles.amtRight}`}>Offers</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr><td colSpan={showPaid ? 5 : 4} className="empty-state">No donors recorded yet.</td></tr>
          ) : filtered.map((d, i) => (
            <tr key={d.userId} className={styles.tr}>
              <td className={styles.td}>
                <span className={`${styles.rankBadge} ${i < 3 ? styles[`rank${i + 1}`] : ''}`}>
                  {i + 1}
                </span>
              </td>
              <td className={styles.td}>{d.memberName || '—'}</td>
              <td className={`${styles.td} ${styles.amtRight}`}>{fmtAmt(d.totalPledged)}</td>
              {showPaid && <td className={`${styles.td} ${styles.amtRight}`}>{fmtAmt(d.totalPaid)}</td>}
              <td className={`${styles.td} ${styles.amtRight}`}>{Number(d.pledgeCount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ContributorsTab = ({ contributors, memberSearch, categoryFilter, showPaid }) => {
  const filtered = useMemo(() => {
    return contributors.filter((c) => {
      const matchMember = !memberSearch.trim() ||
        (c.memberName || '').toLowerCase().includes(memberSearch.toLowerCase());
      const matchCat = !categoryFilter ||
        String(c.donationCategoryId) === String(categoryFilter);
      return matchMember && matchCat;
    });
  }, [contributors, memberSearch, categoryFilter]);

  // colSpan for the footer label cell
  const footerSpan = 4;
  const totalCols = 5 + (showPaid ? 3 : 0);

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>#</th>
            <th className={styles.th}>Member</th>
            <th className={styles.th}>Category</th>
            <th className={styles.th}>Offering Date</th>
            <th className={`${styles.th} ${styles.amtRight}`}>Offered</th>
            {showPaid && <th className={`${styles.th} ${styles.amtRight}`}>Paid</th>}
            {showPaid && <th className={`${styles.th} ${styles.amtRight}`}>Pending</th>}
            {showPaid && <th className={styles.th}>Status</th>}
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr><td colSpan={totalCols} className="empty-state">No contributors recorded yet.</td></tr>
          ) : filtered.map((c, i) => (
            <tr key={c.id} className={styles.tr}>
              <td className={`${styles.td} ${styles.rowNum}`}>{i + 1}</td>
              <td className={styles.td}>{c.memberName || '—'}</td>
              <td className={styles.td}>
                {c.categoryName
                  ? <span className={styles.catPill}>{c.categoryName}</span>
                  : <span className={styles.muted}>—</span>}
              </td>
              <td className={styles.td}>{fmt(c.offeringDate)}</td>
              <td className={`${styles.td} ${styles.amtRight}`}>{fmtAmt(c.offeredAmount)}</td>
              {showPaid && <td className={`${styles.td} ${styles.amtRight}`}>{fmtAmt(c.paidAmount)}</td>}
              {showPaid && <td className={`${styles.td} ${styles.amtRight}`}>{fmtAmt(c.pendingAmount)}</td>}
              {showPaid && <td className={styles.td}><StatusBadge status={c.status} /></td>}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className={styles.tfootRow}>
            <td colSpan={footerSpan} className={`${styles.td} ${styles.tfootLabel}`}>Total ({filtered.length} entries)</td>
            <td className={`${styles.td} ${styles.amtRight} ${styles.tfootAmt}`}>
              {fmtAmt(filtered.reduce((s, c) => s + Number(c.offeredAmount || 0), 0))}
            </td>
            {showPaid && (
              <td className={`${styles.td} ${styles.amtRight} ${styles.tfootAmt}`}>
                {fmtAmt(filtered.reduce((s, c) => s + Number(c.paidAmount || 0), 0))}
              </td>
            )}
            {showPaid && (
              <td className={`${styles.td} ${styles.amtRight} ${styles.tfootAmt}`}>
                {fmtAmt(filtered.reduce((s, c) => s + Number(c.pendingAmount || 0), 0))}
              </td>
            )}
            <td className={styles.td} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const DonationWiseTab = ({ donationWise, categoryFilter, showPaid }) => {
  const filtered = useMemo(() => {
    if (!categoryFilter) return donationWise;
    return donationWise.filter((d) => String(d.categoryId) === String(categoryFilter));
  }, [donationWise, categoryFilter]);

  const grandTotal = useMemo(
    () => filtered.reduce((s, d) => s + Number(d.totalPledged || 0), 0),
    [filtered],
  );

  const donationCols = 6 + (showPaid ? 1 : 0);

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>#</th>
            <th className={styles.th}>Donation Category</th>
            <th className={`${styles.th} ${styles.amtRight}`}>Members</th>
            <th className={`${styles.th} ${styles.amtRight}`}>Offers</th>
            <th className={`${styles.th} ${styles.amtRight}`}>Total Offered</th>
            {showPaid && <th className={`${styles.th} ${styles.amtRight}`}>Total Paid</th>}
            <th className={`${styles.th} ${styles.amtRight}`}>% Share</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr><td colSpan={donationCols} className="empty-state">No donation categories recorded yet.</td></tr>
          ) : filtered.map((d, i) => {
            const pct = grandTotal > 0 ? ((Number(d.totalPledged) / grandTotal) * 100).toFixed(1) : '0.0';
            return (
              <tr key={d.categoryId ?? i} className={styles.tr}>
                <td className={`${styles.td} ${styles.rowNum}`}>{i + 1}</td>
                <td className={styles.td}>
                  <span className={styles.catPill}>{d.categoryName}</span>
                </td>
                <td className={`${styles.td} ${styles.amtRight}`}>{Number(d.memberCount)}</td>
                <td className={`${styles.td} ${styles.amtRight}`}>{Number(d.pledgeCount)}</td>
                <td className={`${styles.td} ${styles.amtRight}`}>{fmtAmt(d.totalPledged)}</td>
                {showPaid && <td className={`${styles.td} ${styles.amtRight}`}>{fmtAmt(d.totalPaid)}</td>}
                <td className={`${styles.td} ${styles.amtRight}`}>
                  <div className={styles.pctWrap}>
                    <span>{pct}%</span>
                    <div className={styles.pctBar}>
                      <div className={styles.pctFill} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className={styles.tfootRow}>
            <td colSpan={4} className={`${styles.td} ${styles.tfootLabel}`}>Total ({filtered.length} entries)</td>
            <td className={`${styles.td} ${styles.amtRight} ${styles.tfootAmt}`}>{fmtAmt(grandTotal)}</td>
            {showPaid && (
              <td className={`${styles.td} ${styles.amtRight} ${styles.tfootAmt}`}>
                {fmtAmt(filtered.reduce((s, d) => s + Number(d.totalPaid || 0), 0))}
              </td>
            )}
            <td className={styles.td} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// -- Donation Count Tab --------------------------------------------------------

const DonationCountTab = ({ donationCount, categoryFilter, memberSearch }) => {
  const members = useMemo(() => {
    if (!categoryFilter) return [];
    return donationCount.filter((row) => {
      const matchCat    = String(row.categoryId) === String(categoryFilter);
      const matchMember = !memberSearch.trim() ||
        (row.memberName || '').toLowerCase().includes(memberSearch.toLowerCase());
      return matchCat && matchMember;
    });
  }, [donationCount, categoryFilter, memberSearch]);

  const selectedGroup = useMemo(() => {
    if (!categoryFilter) return null;
    const first = donationCount.find((r) => String(r.categoryId) === String(categoryFilter));
    return first ? { categoryName: first.categoryName, fixedRate: first.fixedRate } : null;
  }, [donationCount, categoryFilter]);

  const totalCount  = members.reduce((s, m) => s + Number(m.totalCount  || 0), 0);
  const totalAmount = members.reduce((s, m) => s + Number(m.totalAmount || 0), 0);

  if (!categoryFilter) {
    return (
      <div className={styles.emptyState}>
        <FiTag size={36} />
        <p>Select a donation from the filter above to see the count breakdown.</p>
      </div>
    );
  }

  return (
    <div className={styles.tableWrap}>
      {selectedGroup && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px' }}>
          <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--color-text)' }}>
            {selectedGroup.categoryName}
          </span>
          <span className={styles.catPill}>Rate: {fmtAmt(selectedGroup.fixedRate)} / slot</span>
        </div>
      )}
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>#</th>
            <th className={styles.th}>Member</th>
            <th className={`${styles.th} ${styles.amtRight}`}>Count</th>
            <th className={`${styles.th} ${styles.amtRight}`}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {members.length === 0 ? (
            <tr><td colSpan={4} className="empty-state">No records found.</td></tr>
          ) : members.map((m, i) => (
            <tr key={m.personId} className={styles.tr}>
              <td className={`${styles.td} ${styles.rowNum}`}>{i + 1}</td>
              <td className={styles.td}>{m.memberName || '—'}</td>
              <td className={`${styles.td} ${styles.amtRight}`}>{Number(m.totalCount)}</td>
              <td className={`${styles.td} ${styles.amtRight}`}>{fmtAmt(m.totalAmount)}</td>
            </tr>
          ))}
        </tbody>
        {members.length > 0 && (
          <tfoot>
            <tr className={styles.tfootRow}>
              <td colSpan={2} className={`${styles.td} ${styles.tfootLabel}`}>
                Total ({members.length} member{members.length !== 1 ? 's' : ''})
              </td>
              <td className={`${styles.td} ${styles.amtRight} ${styles.tfootAmt}`}>{totalCount}</td>
              <td className={`${styles.td} ${styles.amtRight} ${styles.tfootAmt}`}>{fmtAmt(totalAmount)}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

// -- Main Page -----------------------------------------------------------------

const EventContributionsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeDerasarId, isDerasarAdmin, user } = useAuth();
  const showPaid = isDerasarAdmin || user?.role === 'super_admin' || user?.role === 'platform_admin';

  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState('top-donors');
  const [memberSearch, setMemberSearch]   = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => {
    if (!activeDerasarId) return;
    setLoading(true);
    getEventContributions(id, activeDerasarId)
      .then((res) => setData(res.data))
      .catch(() => toast.error('Failed to load event contributions'))
      .finally(() => setLoading(false));
  }, [id, activeDerasarId]);

  // Unique categories derived from contributors list (for contributors / donation-wise filters)
  const categories = useMemo(() => {
    if (!data) return [];
    const seen = new Map();
    data.contributors.forEach((c) => {
      if (c.donationCategoryId && !seen.has(c.donationCategoryId))
        seen.set(c.donationCategoryId, c.categoryName || 'Unknown');
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [data]);

  // Fixed-rate donation categories for the donation-count tab
  const countCategories = useMemo(() => {
    if (!data) return [];
    const seen = new Map();
    (data.donationCount || []).forEach((r) => {
      if (r.categoryId && !seen.has(r.categoryId))
        seen.set(r.categoryId, r.categoryName || 'Unknown');
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [data]);

  const switchTab = (tabId) => {
    setActiveTab(tabId);
    setMemberSearch('');
    setCategoryFilter('');
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!data)   return null;

  const { event, summary, contributors, topDonors, donationWise, donationCount = [] } = data;

  const renderTab = () => {
    switch (activeTab) {
      case 'top-donors':
        return <TopDonorsTab donors={topDonors} memberSearch={memberSearch} showPaid={showPaid} />;
      case 'contributors':
        return <ContributorsTab contributors={contributors} memberSearch={memberSearch} categoryFilter={categoryFilter} showPaid={showPaid} />;
      case 'donation-wise':
        return <DonationWiseTab donationWise={donationWise} categoryFilter={categoryFilter} showPaid={showPaid} />;
      case 'donation-count':
        return <DonationCountTab donationCount={donationCount} categoryFilter={categoryFilter} memberSearch={memberSearch} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.page}>
      {/* Back nav */}
      <button className={styles.backBtn} onClick={() => navigate('/derasar/events')}>
        <FiArrowLeft size={15} /> Back to Events
      </button>

      {/* Event header — centered */}
      <div className={styles.eventHeader}>
        <h2 className={styles.eventTitle}>{event.title}</h2>
        {event.description && <p className={styles.eventDesc}>{event.description}</p>}
        <div className={styles.eventMeta}>
          <span><FiCalendar size={13} /> Event: <strong>{fmt(event.eventDate)}</strong></span>
          <span><FiClock size={13} /> Contributions: <strong>{fmt(event.contributionStartDate)}</strong> – <strong>{fmt(event.contributionEndDate)}</strong></span>
        </div>
      </div>

      {/* Summary stat cards — centered */}
      <div className={`${styles.statGrid} ${!showPaid ? styles.statGrid3 : ''}`}>
        <StatCard icon={FiUsers}      label="Members Contributing" value={Number(summary.memberCount)}  accent />
        <StatCard icon={FiBarChart2}  label="Total Offers"         value={Number(summary.pledgeCount)} />
        <StatCard icon={FiTrendingUp} label="Total Offered"        value={fmtAmt(summary.totalPledged)} accent />
        {showPaid && <StatCard icon={FiTrendingUp} label="Total Paid" value={fmtAmt(summary.totalPaid)} />}
      </div>

      <div className={styles.card}>
        {/* Tab bar */}
        <div className={styles.tabBar}>
          {TABS.map((t) => (
            <button
              key={t.id}
              className={`${styles.tab} ${activeTab === t.id ? styles.tabActive : ''}`}
              onClick={() => switchTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className={styles.filterRow}>
          {(activeTab === 'contributors' || activeTab === 'donation-wise') && (
            <div className={styles.filterGroup}>
              <FiTag size={14} className={styles.filterIcon} />
              <select
                className={styles.filterSelect}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
          {activeTab === 'donation-count' && (
            <div className={styles.filterGroup}>
              <FiTag size={14} className={styles.filterIcon} />
              <select
                className={styles.filterSelect}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">Select Donation…</option>
                {countCategories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
          {(activeTab === 'top-donors' || activeTab === 'contributors' || activeTab === 'donation-count') && (
            <div className={styles.filterGroup}>
              <FiSearch size={14} className={styles.filterIcon} />
              <input
                className={styles.filterInput}
                type="text"
                placeholder="Search by member name…"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Tab content */}
        <div className={styles.tabContent}>
          {renderTab()}
        </div>
      </div>
    </div>
  );
};

export default EventContributionsPage;
